import api from "@/app/api";
import { useApiCall } from "@/app/api/apiCall";
import { UploadedFile } from "@/app/api/types";
import { CustomField } from "@/app/components/form-fields/atoms";
import { Entity, IFileField } from "@/app/fields/types";
import { useStandardToast } from "@/app/hooks/useStandardToast";
import { Button, CloseButton, Image } from "@chakra-ui/react";
import { FileUpload, FileUploadTrigger } from "@saas-ui/file-upload";
import { LoadingSpinner } from "@saas-ui/react";
import React, { useEffect, useState } from "react";

export const FileFormField = <T extends Entity>({
  field,
  isDisabled,
}: {
  field: IFileField<T>;
  isDisabled?: boolean;
}) => {
  return (
    <CustomField
      field={field}
      render={({ field: { onChange, value } }) => {
        return (
          <Container>
            <FileFormFieldInner
              onChange={onChange}
              file={value}
              field={field}
              isDisabled={isDisabled}
            />
          </Container>
        );
      }}
      isDisabled={isDisabled}
    />
  );
};

export const FileFormFieldInner = <T extends Entity>({
  field,
  isDisabled,
  file,
  onChange,
}: {
  field: IFileField<T>;
  isDisabled?: boolean;
  file: UploadedFile;
  onChange: (value: UploadedFile | null) => void;
}) => {
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const handle = useApiCall();
  const toast = useStandardToast();

  if (!!file) {
    return (
      <DisplayFile
        file={file}
        onDelete={() => onChange(null)}
        isDisabled={isDisabled}
      />
    );
  }

  const uploadFile = async (toUpload: File) => {
    await handle(
      () => api.uploadFile(toUpload),
      ({ url }) => {
        onChange(url);
        setIsUploading(false);
      }
    );
  };

  return (
    <FileUpload
      isDisabled={isDisabled}
      maxFileSize={1024 * 1024 * 5}
      maxFiles={1}
      accept="image/*"
      name={String(field.field)}
      required={field.is_required}
    >
      {({ acceptedFiles, rejectedFiles, ...rest }) => {
        useEffect(() => {
          if (!isUploading && !!acceptedFiles.length && !file) {
            setIsUploading(true);
            uploadFile(acceptedFiles[0]);
          }
        }, [acceptedFiles, isUploading, file]);

        useEffect(() => {
          if (rejectedFiles.length) {
            toast({
              title: `${rejectedFiles[0].errors[0]}`,
              status: "error",
            });
          }
        }, [rejectedFiles]);

        return isUploading ? (
          <LoadingSpinner />
        ) : (
          <FileUploadTrigger as={Button} disabled={isDisabled}>
            Select file
          </FileUploadTrigger>
        );
      }}
    </FileUpload>
  );
};

const DisplayFile = ({
  file,
  isDisabled,
  onDelete,
}: {
  file: UploadedFile;
  isDisabled?: boolean;
  onDelete: () => void;
}) => (
  <div className="flex gap-2 items-center">
    <Image
      className="max-h-12 max-w-16 rounded"
      src={file}
      alt="uploaded image"
    />
    <CloseButton
      onClick={onDelete}
      disabled={isDisabled}
      data-cy="remove-file-button"
    />
  </div>
);

const Container = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      className="border border-dashed p-4 flex flex-col justify-center items-center gap-2"
      style={{
        height: "var(--chakra-sizes-20)",
        borderRadius: "var(--chakra-radii-md)",
        borderColor: "var(--chakra-colors-blackAlpha-300)",
      }}
    >
      {children}
    </div>
  );
};
